// src/components/JoinOrgHandler.jsx

import React, { useState, useEffect, useRef } from 'react';
import { 
    Building2, CheckCircle2, LogIn, UserPlus, ArrowRight, MapPin, 
    User, Calendar, Globe, LogOut, ShieldCheck, FileText, X, Info, Users 
} from 'lucide-react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, functions } from '../config/firebase-config';
import { httpsCallable } from 'firebase/functions';
import { ageTier } from '../utils/ageTier';
import { resolveMemberTypes } from '../data/memberTypes';
import { isCorporateOrg } from '../data/copyDictionary';
import { divisionOf, divisionsIn, teamsInDivision, ageNoteFor } from '../utils/teams';

// --- GEOGRAPHIC CONSTANTS ---
const COUNTRIES = ["Canada", "United States", "United Kingdom", "Australia", "New Zealand", "Germany", "France", "Spain", "Italy", "Japan", "China", "India", "Brazil", "Mexico", "Other"];
const CAN_PROVINCES = ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Northwest Territories", "Nunavut", "Yukon"];
const US_STATES = ["Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"];

// --- CONSENT TEXTS ---
const CONSENT_TEXTS = {
    anonymous: {
        title: "Anonymous Data Collection Notice",
        content: `
**Purpose of Data Collection:**
We collect data to improve the performance and wellness insights provided by the ERI HUB platform. This data helps us understand trends, optimize training protocols, and provide personalized feedback.

**What We Collect:**
- Assessment scores (e.g., focus, resilience, sleep quality).
- Usage data (e.g., modules accessed, frequency of use).
- Basic demographic information (age, gender, region) for aggregate analysis.

**Anonymity & Privacy:**
- Your personal identity (name, email) is kept strictly confidential.
- Data used for broad analysis or research is stripped of all personal identifiers (anonymized).
- We do not sell your personal data to third parties.
- Your data is stored securely using industry-standard encryption.
        `
    },
    parental: {
        title: "Parental Consent & Participant Agreement",
        content: `
**For Athletes Under 18:**
This platform provides mental performance training and wellness monitoring. It is not a substitute for professional medical or psychological advice.

**Parent/Guardian Agreement:**
- I verify that I am the parent or legal guardian of the participant.
- I consent to my child participating in the ERI HUB platform assessments and activities.
- I understand that the platform collects performance metrics and self-reported wellness data to help coaches and athletes optimize performance.
- I acknowledge that if my child reports significant distress, it is my responsibility to seek appropriate professional support. ERI HUB provides information, not clinical diagnosis.

**Participant Agreement:**
- I agree to answer questions honestly to the best of my ability.
- I understand that this tool is to help me grow as an athlete and a person.
        `
    }
};

// "A", "A and B", "A, B and C".
const listTeams = (teams = []) => teams.length <= 1 ? (teams[0] || '') : `${teams.slice(0, -1).join(', ')} and ${teams[teams.length - 1]}`;

// GUARDIAN CONSENT (under-18s). The record Hockey Alberta can stand behind:
// who consented, for whom, when, under which text. Contact details go to a
// self-only doc (users/{uid}/private_profile/guardian, firestore.rules); the
// user doc, which coaches can read, carries only that consent exists, when,
// and the relationship. Prefilled for 20 minutes after "Add another player"
// so a parent with two kids types it once.
const CONSENT_VERSION = '2026-10';
const GUARDIAN_PREFILL_KEY = 'eas_guardian_prefill';
const EMPTY_GUARDIAN = { name: '', email: '', phone: '', relationship: 'Parent', consent: false, emailOptIn: true };
const readGuardianPrefill = () => {
    try {
        const raw = sessionStorage.getItem(GUARDIAN_PREFILL_KEY);
        if (!raw) return EMPTY_GUARDIAN;
        const saved = JSON.parse(raw);
        if (saved && saved.until && Date.now() < saved.until && saved.guardian) {
            return { ...EMPTY_GUARDIAN, ...saved.guardian, consent: false };
        }
    } catch (e) { /* storage unavailable */ }
    return EMPTY_GUARDIAN;
};
const saveGuardianPrefill = (g) => {
    try {
        const { name, email, phone, relationship, emailOptIn } = g;
        sessionStorage.setItem(GUARDIAN_PREFILL_KEY, JSON.stringify({ until: Date.now() + 20 * 60 * 1000, guardian: { name, email, phone, relationship, emailOptIn } }));
    } catch (e) { /* storage unavailable */ }
};
const EMAIL_OK = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

// TEAM PICKER. From a team's own link the team is locked and shown, nothing to
// choose. From the plain org link: division first (U13 / U15 / U18, read off
// the team names), then only that division's teams. An age that does not fit
// the division gets a note, never a block: call-ups happen.
const TeamPicker = ({ groups, value, onChange, age, locked }) => {
    const divisions = divisionsIn(groups);
    const hasDivisions = divisions.some(d => d !== 'Other');
    const [division, setDivision] = useState(() => (value ? divisionOf(value) : (divisions.length === 1 ? divisions[0] : '')));
    const note = value ? ageNoteFor(divisionOf(value), age) : null;
    const field = 'w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary';
    if (locked) {
        return (
            <div className="rounded-2xl border border-brand-primary/30 bg-brand-light/40 p-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-brand-primary">You're joining</p>
                <p className="text-lg font-black text-slate-900 leading-tight mt-0.5">{value}</p>
                <p className="text-[11px] text-slate-500 font-medium mt-1">Wrong team? Ask your coach or team manager for your team's link.</p>
                {note && <p className="text-[11px] text-amber-700 font-medium mt-2 leading-snug">{note}</p>}
            </div>
        );
    }
    return (
        <div className="space-y-3">
            {hasDivisions && (
                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Division <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-3 gap-2">
                        {divisions.map(d => (
                            <button key={d} type="button" onClick={() => { setDivision(d); onChange(''); }}
                                className={`py-3 rounded-xl font-black text-sm border-2 transition-colors ${division === d ? 'bg-brand-primary border-brand-primary text-white' : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-brand-primary/50'}`}>
                                {d}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {(division || !hasDivisions) && (
                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Your team <span className="text-red-500">*</span></label>
                    <div className="relative">
                        <Users size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <select className={field} value={value} onChange={e => onChange(e.target.value)} required>
                            <option value="" disabled>Choose your team...</option>
                            {(hasDivisions ? teamsInDivision(groups, division) : groups).map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </div>
                </div>
            )}
            {note && <p className="text-[11px] text-amber-700 font-medium leading-snug">{note}</p>}
        </div>
    );
};

const TermsModal = ({ type, onClose }) => {
    const data = CONSENT_TEXTS[type];
    if (!data) return null;

    return (
        <div className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <FileText size={20} className="text-blue-600" /> {data.title}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                        <X size={20} className="text-slate-500" />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto whitespace-pre-line text-sm text-slate-600 leading-relaxed">
                    {data.content}
                </div>
                <div className="p-4 border-t border-slate-100 bg-slate-50 text-right">
                    <button onClick={onClose} className="px-6 py-2 bg-slate-900 text-white rounded-lg font-bold text-sm hover:bg-black transition-colors">
                        Close & Continue
                    </button>
                </div>
            </div>
        </div>
    );
};

const JoinOrgHandler = ({ orgId, team = null, staffCode = null, onComplete }) => {
    const [step, setStep] = useState('loading');
    // A STAFF LINK (#join/{org}/{team}/staff/{code}): bench staff join onto the
    // team's coaches roster. The claim is server-side (claimStaffSeat) right after
    // sign-in; from then on the person reads as a roster coach here, and
    // syncMemberRole gives the role once the email is verified.
    const staffMode = !!staffCode;
    const [orgName, setOrgName] = useState('Team');
    // A workplace's under-18 is a child, not a player (the sport word everywhere else).
    const [orgIsCorporate, setOrgIsCorporate] = useState(false);
    const kid = orgIsCorporate ? 'child' : 'player';
    const [orgAdminList, setOrgAdminList] = useState([]);
    const [orgCoachList, setOrgCoachList] = useState([]); // Array of { email, groupId }
    // The staff claim, as a ref as well as state: getRoleContext runs inside the
    // same async step as the claim, before React re-renders.
    const staffClaimRef = useRef(null);
    const [staffClaim, setStaffClaim] = useState(null);
    const recordStaffClaim = (claim) => { staffClaimRef.current = claim; setStaffClaim(claim); };
    
    // Squad / Feature State
    const [orgGroups, setOrgGroups] = useState([]);
    const [isSquadHubEnabled, setIsSquadHubEnabled] = useState(false);
    const [selectedSquad, setSelectedSquad] = useState('');

    // Member-funnel: relationship lens (self-selected) + the catalogue for this org
    const [orgMemberTypes, setOrgMemberTypes] = useState([]);
    const [selectedMemberType, setSelectedMemberType] = useState(null);

    const [isLoginMode, setIsLoginMode] = useState(false);
    const [currentUserEmail, setCurrentUserEmail] = useState(null);
    // The signed-in person's profile, when they already have one here: a member
    // who opens a join link again is told they are in, not asked to join again.
    const [existingProfile, setExistingProfile] = useState(null);
    
    // Auth State
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [tempUid, setTempUid] = useState(null);
    
    // NEW: Password Reset State
    const [isResetMode, setIsResetMode] = useState(false);
    const [resetMessage, setResetMessage] = useState('');
    const [resetMessageType, setResetMessageType] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    // NEW: Password Reset Handler
    const handlePasswordReset = async (e) => {
        e.preventDefault();
        if (!email) {
            setResetMessage("Please enter your email address first.");
            setResetMessageType('error');
            return;
        }
        setResetMessage('');
        setIsProcessing(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setResetMessage(`Reset link sent to ${email}. Check your inbox!`);
            setResetMessageType('success');
        } catch (err) {
            setResetMessage("Reset Failed: " + err.message);
            setResetMessageType('error');
        } finally {
            setIsProcessing(false);
        }
    };

    // Profile State
    const [fullName, setFullName] = useState('');
    const [age, setAge] = useState('');
    const [gender, setGender] = useState('');
    const [country, setCountry] = useState('');
    const [region, setRegion] = useState('');
    
    // Consent State
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [guardian, setGuardian] = useState(readGuardianPrefill);
    const [joinedMinor, setJoinedMinor] = useState(false);
    const [joinedAs, setJoinedAs] = useState('member'); // 'member' | 'coach' | 'admin': what the success screen offers
    const [showTermsModal, setShowTermsModal] = useState(null);

    const [errorMsg, setErrorMsg] = useState('');

    // 1. Initial Check & Fetch Org Data
    useEffect(() => {
        const checkContext = async () => {
            try {
                const orgSnap = await getDoc(doc(db, "organizations", orgId));
                if (orgSnap.exists()) {
                    const data = orgSnap.data();
                    setOrgName(data.name);
                    setOrgIsCorporate(isCorporateOrg(data));
                    
                    const admins = (data.adminEmails || []).map(e => e.toLowerCase().trim());
                    setOrgAdminList(admins);

                    // --- PARSE STRUCTURED COACHES ---
                    // Expected format from DB: [{ email: "...", groupId: "..." }]
                    let coaches = [];
                    if (data.coaches && Array.isArray(data.coaches)) {
                        coaches = data.coaches.map(c => ({
                            ...c,
                            email: (c.email || '').toLowerCase().trim()
                        }));
                    }
                    setOrgCoachList(coaches);
                    
                    // Squad Feature Context
                    setOrgGroups(data.groups || []);
                    // A team's own link pre-selects and locks the team.
                    if (team && (data.groups || []).includes(team)) setSelectedSquad(team);
                    setIsSquadHubEnabled(data.features?.enableSquadHub || false);
                    setOrgMemberTypes(resolveMemberTypes(data));
                    // The organization's default location (org editor): a Hockey Alberta
                    // member is in Alberta, so the selects start there, still editable.
                    if (data.defaultLocation?.country) {
                        setCountry(data.defaultLocation.country);
                        setRegion(data.defaultLocation.region || '');
                    }
                    // A staff link must name one of the organization's teams.
                    if (staffMode && !(team && (data.groups || []).includes(team))) {
                        setErrorMsg("This staff link doesn't match one of the organization's teams. Ask your team contact for the current one.");
                        setStep('error');
                        return;
                    }
                } else {
                    // Org doc is gone/never existed — stop here so a bogus link
                    // can never create a half-joined account.
                    setErrorMsg("This invite link is invalid or expired. Please check with your administrator.");
                    setStep('error');
                    return;
                }

                const user = auth.currentUser;
                if (user) {
                    setCurrentUserEmail(user.email);
                    try {
                        const mine = await getDoc(doc(db, "users", user.uid));
                        setExistingProfile(mine.exists() ? mine.data() : null);
                    } catch (e) { setExistingProfile(null); }
                    setStep('confirm_user');
                } else {
                    setStep('auth');
                }
            } catch (error) {
                if (error.code === 'permission-denied') setStep('auth');
                else { setErrorMsg("Invalid Invite Link."); setStep('error'); }
            }
        };
        checkContext();
    // `team` arrives with the link and never changes while mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    // --- HELPER TO DETERMINE ACTIVE ROLE ---
    const getRoleContext = (checkEmail) => {
        const cleanEmail = checkEmail ? checkEmail.toLowerCase().trim() : '';
        const isAdmin = orgAdminList.includes(cleanEmail);
        const claimed = staffClaimRef.current || staffClaim;
        // Every roster entry for this email (a coach may be on several teams) plus a
        // staff claim made just now; the first is the primary groupId.
        const rosterTeams = orgCoachList.filter(c => c.email === cleanEmail && c.groupId).map(c => c.groupId);
        const claimedTeam = claimed && claimed.email === cleanEmail ? claimed.groupId : null;
        const coachTeams = [...new Set([...rosterTeams, ...(claimedTeam ? [claimedTeam] : [])])];
        const matchedCoach = coachTeams.length ? { email: cleanEmail, groupId: coachTeams[0], teams: coachTeams } : undefined;
        return {
            cleanEmail,
            isAdmin,
            isCoach: !!matchedCoach,
            matchedCoach
        };
    };

    // A member profile that's missing an age signal (ageTier or age) or consent
    // must go through the persona/profile onboarding before joining.
    const profileNeedsOnboarding = (data) => !data || (!data.ageTier && (data.age === undefined || data.age === null)) || !data.consentAccepted;

    const handleSwitchAccount = async () => { await signOut(auth); setStep('auth'); };

    const handleConfirmJoin = async () => {
        if (staffMode) {
            try { await claimStaffSeat(currentUserEmail); } catch (e) { setErrorMsg(e.message); return; }
        }
        const { isAdmin, isCoach } = getRoleContext(currentUserEmail);

        // Validation for normal users
        if (isSquadHubEnabled && orgGroups.length > 0 && !isCoach && !isAdmin && !selectedSquad) {
            setErrorMsg("Please select your squad/team to continue.");
            return;
        }

        // Members with no age gate / consent on file must complete onboarding
        // before joining (staff keep their direct-join path).
        if (!isAdmin && !isCoach) {
            const profileSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
            if (profileNeedsOnboarding(profileSnap.exists() ? profileSnap.data() : null)) {
                setTempUid(auth.currentUser.uid);
                setEmail(auth.currentUser.email || '');
                setStep('persona');
                return;
            }
        }

        setStep('joining');
        await joinUserToOrg(auth.currentUser.uid, auth.currentUser.email);
    };

    // Staff roles come from the server and need a verified email. Ask for the
    // role; if it is held, send the verification mail and tell the person.
    const [verifyNotice, setVerifyNotice] = useState(null); // { email, roleLabel }
    const requestStaffRole = async () => {
        try {
            const res = await httpsCallable(functions, 'syncMemberRole')({});
            if (res.data?.needsVerification && auth.currentUser) {
                try { await sendEmailVerification(auth.currentUser); } catch (e) { /* banner in the app offers Resend */ }
                setVerifyNotice({ email: auth.currentUser.email, roleLabel: res.data.pendingRole === 'admin' ? 'admin' : 'coach' });
            }
        } catch (e) { console.error('Role sync failed:', e); }
    };

    // Staff link: name this email on the team's coaches roster. The code is
    // checked server-side. Resolves to the team, or throws with a line to show.
    const claimStaffSeat = async (userEmail) => {
        try {
            const res = await httpsCallable(functions, 'claimStaffSeat')({ orgId, groupId: team, code: staffCode });
            const groupId = res.data?.groupId || team;
            recordStaffClaim({ email: (userEmail || '').toLowerCase().trim(), groupId });
            return groupId;
        } catch (e) {
            const generic = 'This staff link could not be used. Ask your team contact for the current one.';
            const err = new Error(e && e.message && e.message.toLowerCase() !== 'internal' ? e.message : generic);
            err.code = 'staff/claim';
            throw err;
        }
    };

    // 2. Logic to Stamp Organization ID & ASSIGN ROLE
    const joinUserToOrg = async (uid, userEmail) => {
        try {
            const { isAdmin, isCoach, matchedCoach } = getRoleContext(userEmail);

            const payload = {
                organizationId: orgId,
                joinedAt: new Date().toISOString()
            };

            // Add Group Tag if feature enabled
            if (isSquadHubEnabled) {
                if (isCoach && matchedCoach?.groupId) {
                    payload.groupId = matchedCoach.groupId; // Auto-assign Coach Group
                } else if (selectedSquad) {
                    payload.groupId = selectedSquad; // Normal User Group
                }
            }

            // Members write themselves in as plain users. Staff roles come from the
            // server (syncMemberRole checks the auth email against the org's rosters);
            // the rules refuse a self-written admin or coach role.
            if (!isAdmin && !isCoach) payload.role = 'user';

            await setDoc(doc(db, "users", uid), payload, { merge: true });
            if (isAdmin || isCoach) await requestStaffRole();
            setJoinedAs(isAdmin ? 'admin' : isCoach ? 'coach' : 'member');
            setStep('success');
        } catch (e) {
            setErrorMsg("Join failed: " + e.message);
            setStep('error');
        }
    };

    // 3. Step A: Authentication
    const handleAuthSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');
        
        try {
            let userCredential;
            if (isLoginMode) {
                userCredential = await signInWithEmailAndPassword(auth, email, password);
                if (staffMode) await claimStaffSeat(userCredential.user.email);

                const { isCoach, isAdmin } = getRoleContext(userCredential.user.email);

                // If logging in, Squads enabled, and user is NOT a coach or admin, detour to pick squad
                if (isSquadHubEnabled && orgGroups.length > 0 && !selectedSquad && !isCoach && !isAdmin) {
                    setCurrentUserEmail(userCredential.user.email);
                    setStep('confirm_user');
                } else {
                    // Members with no age gate / consent on file must complete
                    // onboarding before joining (staff keep their direct-join path).
                    if (!isAdmin && !isCoach) {
                        const profileSnap = await getDoc(doc(db, "users", userCredential.user.uid));
                        if (profileNeedsOnboarding(profileSnap.exists() ? profileSnap.data() : null)) {
                            setTempUid(userCredential.user.uid);
                            setStep('persona');
                            return;
                        }
                    }
                    setStep('joining');
                    await joinUserToOrg(userCredential.user.uid, userCredential.user.email);
                }
            } else {
                userCredential = await createUserWithEmailAndPassword(auth, email, password);
                // Recoverable stub so an abandoned signup isn't an orphaned
                // auth account with no Firestore doc.
                await setDoc(doc(db, "users", userCredential.user.uid), {
                    email: userCredential.user.email.toLowerCase().trim(),
                    organizationId: orgId,
                    onboardingComplete: false,
                    // The team the link named, so a join resumed later lands on it.
                    joinedFromTeam: team || null,
                    createdAt: new Date().toISOString()
                }, { merge: true });
                setTempUid(userCredential.user.uid);
                if (staffMode) await claimStaffSeat(userCredential.user.email);
                // Staff are auto-detected by email and skip the persona step;
                // members pick who they are first.
                const { isAdmin, isCoach } = getRoleContext(userCredential.user.email);
                setStep(isAdmin || isCoach ? 'profile' : 'persona');
            }
        } catch (error) {
            let friendlyMessage = "An unexpected error occurred. Please try again.";
            
            if (error.code === 'auth/invalid-credential') {
                friendlyMessage = "Incorrect email or password. Please try again.";
            } else if (error.code === 'auth/email-already-in-use') {
                friendlyMessage = "An account with this email already exists. Please sign in instead.";
            } else if (error.code === 'auth/weak-password') {
                friendlyMessage = "Password is too weak. Please use at least 6 characters.";
            } else if (error.code === 'staff/claim') {
                // The account exists (or was just made); the link is the problem.
                friendlyMessage = error.message + (isLoginMode ? '' : ' Your account is created: open the current staff link and log in.');
            }

            setErrorMsg(friendlyMessage);
        }
    };

    // A parent with a second child on another team: sign the first player out and
    // start again at the account step. The guardian details carry over (prefill);
    // the consent tick does not - it is given per player.
    const handleAddAnotherPlayer = async () => {
        try { await signOut(auth); } catch (e) { /* already out */ }
        setEmail(''); setPassword(''); setTempUid(null); setIsLoginMode(false);
        setFullName(''); setAge(''); setGender(''); setSelectedSquad(''); setSelectedMemberType(null);
        setTermsAccepted(false); setJoinedMinor(false); setErrorMsg('');
        setGuardian(g => ({ ...g, consent: false }));
        setStep('auth');
    };

    // Writes the new user's profile (incl. the two funnel fields) and joins the org.
    const finalizeJoin = async (validatedAge, tier) => {
        try {
            const cleanEmail = email.toLowerCase().trim();
            const { isAdmin, isCoach, matchedCoach } = getRoleContext(cleanEmail);

            let assignedRole = 'user';
            if (isAdmin) assignedRole = 'admin';
            else if (isCoach) assignedRole = 'coach';

            let finalGroupId = null;
            if (isSquadHubEnabled) {
                if (isCoach && matchedCoach?.groupId) finalGroupId = matchedCoach.groupId;
                else finalGroupId = selectedSquad || null;
            }

            // This path can now run for EXISTING accounts (returning users sent
            // back through onboarding) — merge, and only seed new-account fields
            // when they're not already on the doc, so streak/history survive.
            const existingSnap = await getDoc(doc(db, "users", tempUid));
            const existingData = existingSnap.exists() ? existingSnap.data() : null;

            const payload = {
                name: fullName.trim(),
                email: cleanEmail,
                age: validatedAge,
                gender: gender,
                country: country,
                region: (country === 'Canada' || country === 'United States') ? region : null,
                organizationId: orgId,
                groupId: finalGroupId,
                ...(assignedRole === 'user' ? { role: 'user' } : {}), // staff roles are set by syncMemberRole below
                memberType: selectedMemberType, // funnel: relationship lens (null for staff)
                ageTier: tier,                  // funnel: content gate
                consentAccepted: true,
                consentDate: new Date().toISOString(),
                onboardingComplete: true
            };
            if (!existingData || existingData.createdAt === undefined) {
                payload.createdAt = new Date().toISOString();
            }
            if (!existingData || existingData.pulseStreak === undefined) {
                payload.pulseStreak = 0;
                payload.lastPulseDate = null;
            }

            const minor = (tier === 'kid' || tier === 'teen') && assignedRole === 'user';
            const guardianRecord = minor ? {
                name: guardian.name.trim(),
                email: guardian.email.trim().toLowerCase(),
                phone: guardian.phone.trim(),
                relationship: guardian.relationship || 'Parent',
                emailOptIn: !!guardian.emailOptIn,
                consentAt: new Date().toISOString(),
                consentVersion: CONSENT_VERSION,
                playerName: fullName.trim(),
                playerAge: validatedAge
            } : null;
            if (guardianRecord) {
                // What staff may see: that consent exists, when, by whom. No contact details.
                payload.guardianConsent = { at: guardianRecord.consentAt, relationship: guardianRecord.relationship, emailOptIn: guardianRecord.emailOptIn, version: CONSENT_VERSION };
            }

            await setDoc(doc(db, "users", tempUid), payload, { merge: true });
            if (assignedRole !== 'user') await requestStaffRole();
            if (guardianRecord) {
                await setDoc(doc(db, 'users', tempUid, 'private_profile', 'guardian'), guardianRecord, { merge: true });
                saveGuardianPrefill(guardian);
                setJoinedMinor(true);
            }

            setStep('joining');
            await joinUserToOrg(tempUid, cleanEmail);
        } catch (error) {
            setErrorMsg("Profile save failed: " + error.message);
            setStep('profile');
        }
    };

    // 4. Step B: Profile Setup — validates, then either confirms the one ambiguous
    // age/persona combo (child + 18+) or finalizes the join.
    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        if (!tempUid) return;

        const validatedAge = parseInt(age);
        if (isNaN(validatedAge) || validatedAge < 5 || validatedAge > 120) {
            setErrorMsg("Please enter a valid age.");
            return;
        }

        const { isAdmin, isCoach } = getRoleContext(email);

        if (isSquadHubEnabled && orgGroups.length > 0 && !isCoach && !isAdmin && !selectedSquad) {
            setErrorMsg("Please select your squad/team to continue.");
            return;
        }
        if (!orgId) {
            setErrorMsg("Invalid organization link. Please contact your administrator.");
            return;
        }
        if (!termsAccepted) {
            setErrorMsg("You must accept the data policy to continue.");
            return;
        }

        const tier = ageTier(validatedAge);

        // Under-18 members need a parent or guardian on record and an explicit
        // consent tick, separate from the data policy.
        if ((tier === 'kid' || tier === 'teen') && !isAdmin && !isCoach) {
            if (!guardian.name.trim() || !guardian.email.trim()) {
                setErrorMsg(`A parent or guardian's name and email are required for a ${kid} under 18.`);
                return;
            }
            if (!EMAIL_OK(guardian.email)) {
                setErrorMsg("That parent or guardian email doesn't look right.");
                return;
            }
            if (!guardian.consent) {
                setErrorMsg('A parent or guardian has to tick the consent box to continue.');
                return;
            }
        }

        // The only ambiguous combo: a "child" who is 18+. Confirm before sending
        // them into the adult experience; everyone else proceeds straight through.
        if (selectedMemberType === 'child' && tier === 'adult') {
            setErrorMsg('');
            setStep('gate_confirm');
            return;
        }

        await finalizeJoin(validatedAge, tier);
    };

    // --- RENDER STATES ---

    // Derive active email for UI rendering checks
    const activeEmail = (step === 'confirm_user' && currentUserEmail) ? currentUserEmail : email;
    const { isAdmin: isRecognizedAdmin, isCoach: isRecognizedCoach, matchedCoach } = getRoleContext(activeEmail);
    // The division's age note is for the player picking a team, not for a parent
    // (an adult slot) giving their own age.
    const selectedSlot = orgMemberTypes.find(mt => mt.id === selectedMemberType);
    const noteAge = selectedSlot && selectedSlot.expects === 'adult' ? null : age;

    if (step === 'loading' || step === 'joining') {
        return (
            <div className="flex flex-col items-center justify-center h-full animate-in fade-in">
                <Building2 size={48} className="text-brand-primary animate-bounce mb-4"/>
                <h2 className="text-xl font-bold text-slate-700">
                    {step === 'loading' ? 'Locating Team...' : `Joining ${orgName}...`}
                </h2>
            </div>
        );
    }

    if (step === 'confirm_user') {
        // Already a member here, profile complete: nothing to join.
        const alreadyIn = !!existingProfile && existingProfile.organizationId === orgId && existingProfile.onboardingComplete !== false;
        const existingRole = existingProfile?.role;
        const existingKind = existingRole === 'admin' || existingRole === 'super_admin' ? 'admin' : existingRole === 'coach' ? 'coach' : 'home';
        if (alreadyIn) {
            const myTeams = Array.isArray(existingProfile.groupIds) && existingProfile.groupIds.length ? existingProfile.groupIds : (existingProfile.groupId ? [existingProfile.groupId] : []);
            const where = existingKind === 'coach' ? (myTeams.length ? `the coach view for ${listTeams(myTeams)}` : 'your team dashboard')
                : existingKind === 'admin' ? 'the console'
                : (existingProfile.groupId ? `${existingProfile.groupId}` : orgName);
            // A coach opening another team's staff link adds that team to their view.
            const canAddTeam = staffMode && existingKind === 'coach' && !!team && !myTeams.includes(team);
            const addTeam = async () => {
                setErrorMsg('');
                try { await claimStaffSeat(currentUserEmail); await requestStaffRole(); onComplete('coach'); }
                catch (e) { setErrorMsg(e.message); }
            };
            return (
                <div className="flex flex-col items-center justify-center h-full p-4 bg-slate-50">
                    <div className="bg-white w-full max-w-md p-8 rounded-3xl shadow-xl border border-slate-200 animate-in slide-in-from-bottom-4 text-center">
                        <div className="inline-block p-4 bg-green-50 text-green-600 rounded-full mb-4">
                            <CheckCircle2 size={32} />
                        </div>
                        <h2 className="text-2xl font-black text-slate-900 mb-2">You're already in</h2>
                        <p className="text-slate-500 mb-2"><span className="font-bold text-slate-800">{currentUserEmail}</span> is a member of {orgName}, on {where}.</p>
                        {errorMsg && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-bold rounded-xl text-center">{errorMsg}</div>}
                        {canAddTeam && (
                            <>
                                <p className="text-xs text-slate-500 mb-4">This link is for {team}. Add it, and your coach view gets a team switcher.</p>
                                <button
                                    onClick={addTeam}
                                    className="w-full py-4 bg-brand-primary text-white rounded-xl font-bold hover:bg-brand-dark transition-all shadow-lg shadow-brand-primary/30 flex justify-center items-center gap-2 mb-4"
                                >
                                    Add {team} to my coach view <ArrowRight size={18} />
                                </button>
                            </>
                        )}
                        {staffMode && existingKind === 'home' && (
                            <p className="text-xs text-slate-500 mb-4">You joined as a member. To join as team staff instead, sign up with a different email from this link, or ask your administrator.</p>
                        )}
                        <button
                            onClick={() => onComplete(existingKind)}
                            className={canAddTeam
                                ? 'w-full py-4 bg-white border-2 border-slate-200 text-slate-700 rounded-xl font-bold hover:border-slate-400 transition-all flex justify-center items-center gap-2 mb-4'
                                : 'w-full py-4 bg-brand-primary text-white rounded-xl font-bold hover:bg-brand-dark transition-all shadow-lg shadow-brand-primary/30 flex justify-center items-center gap-2 mb-4 mt-4'}
                        >
                            {canAddTeam ? 'Not now, open the app' : 'Open the app'} <ArrowRight size={18} />
                        </button>
                        <button
                            onClick={handleSwitchAccount}
                            className="w-full py-4 bg-white border-2 border-slate-200 text-slate-600 rounded-xl font-bold hover:border-slate-400 transition-all flex justify-center items-center gap-2"
                        >
                            <LogOut size={18}/> Not you? Switch account
                        </button>
                    </div>
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center justify-center h-full p-4 bg-slate-50">
                <div className="bg-white w-full max-w-md p-8 rounded-3xl shadow-xl border border-slate-200 animate-in slide-in-from-bottom-4 text-center">
                    <div className="inline-block p-4 bg-blue-50 text-brand-primary rounded-full mb-4">
                        <User size={32} />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 mb-2">You are logged in</h2>
                    <p className="text-slate-500 mb-6">Do you want to join <strong>{orgName}</strong> as <br/><span className="font-bold text-slate-800">{currentUserEmail}</span>?</p>
                    
                    {errorMsg && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-bold rounded-xl text-center">{errorMsg}</div>}

                    {/* Pre-Assigned Coach Badge */}
                    {isRecognizedCoach && (
                        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl text-sm font-bold flex items-center justify-center gap-2 animate-in fade-in">
                            <ShieldCheck size={18} />
                            Team staff for: {listTeams(matchedCoach.teams)}
                        </div>
                    )}
                    {staffMode && !isRecognizedCoach && !isRecognizedAdmin && (
                        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl text-sm font-bold flex items-center justify-center gap-2 animate-in fade-in">
                            <ShieldCheck size={18} />
                            Team staff sign-up for: {team}
                        </div>
                    )}

                    {/* Pre-Assigned Admin Badge */}
                    {isRecognizedAdmin && (
                        <div className="mb-6 p-3 bg-purple-50 border border-purple-200 text-purple-700 rounded-xl text-sm font-bold flex items-center justify-center gap-2 animate-in fade-in">
                            <ShieldCheck size={18} />
                            Global Administrator Access
                        </div>
                    )}

                    {/* Only show Squad selection to normal users if Squad Hub is enabled */}
                    {isSquadHubEnabled && orgGroups.length > 0 && !isRecognizedCoach && !isRecognizedAdmin && !staffMode && (
                        <div className="mb-6 text-left animate-in fade-in">
                            <TeamPicker groups={orgGroups} value={selectedSquad} onChange={setSelectedSquad} age={noteAge} locked={!!(team && orgGroups.includes(team) && selectedSquad === team)} />
                        </div>
                    )}

                    <button 
                        onClick={handleConfirmJoin}
                        className="w-full py-4 bg-brand-primary text-white rounded-xl font-bold hover:bg-brand-dark transition-all shadow-lg shadow-brand-primary/30 flex justify-center items-center gap-2 mb-4"
                    >
                        Yes, Join Team
                    </button>

                    <button 
                        onClick={handleSwitchAccount}
                        className="w-full py-4 bg-white border-2 border-slate-200 text-slate-600 rounded-xl font-bold hover:border-slate-400 transition-all flex justify-center items-center gap-2"
                    >
                        <LogOut size={18}/> No, Switch Account
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center p-8 animate-in zoom-in">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6 shadow-sm">
                    <CheckCircle2 size={48} />
                </div>
                <h2 className="text-3xl font-black text-slate-900 mb-2">Welcome to {orgName}!</h2>
                {verifyNotice ? (
                    <div className="mb-8 max-w-md text-left rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <p className="text-sm font-black text-slate-900">One more step for your {verifyNotice.roleLabel} tools</p>
                        <p className="text-xs text-slate-600 font-medium mt-1">Your email is on the organization's {verifyNotice.roleLabel} list. We've sent a verification link to <span className="font-bold">{verifyNotice.email}</span>. Open it, then use "I've verified" inside the app, and your {verifyNotice.roleLabel === 'admin' ? 'console' : 'team dashboard'} unlocks. Until then you're signed in as a member, with no check-in to do.</p>
                    </div>
                ) : (
                    <p className="text-slate-500 mb-8 max-w-md">
                        {joinedAs === 'coach' ? "You're in. Your team dashboard is ready."
                            : joinedAs === 'admin' ? "You're in. The console is ready."
                            : "You're in. Let's establish your performance baseline."}
                    </p>
                )}
                <button 
                    onClick={() => onComplete(verifyNotice ? 'pending' : joinedAs)}
                    className="px-10 py-4 bg-slate-900 text-white rounded-2xl font-bold hover:scale-105 transition-transform flex items-center gap-2 shadow-xl"
                >
                    {verifyNotice ? 'Open the app' : joinedAs === 'coach' ? 'Go to your team' : joinedAs === 'admin' ? 'Open the console' : 'Start Assessment'} <ArrowRight size={20}/>
                </button>
                {joinedMinor && (
                    <>
                        <button
                            type="button"
                            onClick={handleAddAnotherPlayer}
                            className="mt-4 px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl font-bold hover:bg-slate-50 transition-colors flex items-center gap-2"
                        >
                            <UserPlus size={18} /> Add another {kid}
                        </button>
                        <p className="mt-3 text-xs text-slate-400 max-w-sm">Another child on a different team? Your parent details carry over for the next few minutes, so you only type them once.</p>
                    </>
                )}
            </div>
        );
    }

    if (step === 'auth') {
        return (
            <div className="flex flex-col items-center justify-center h-full p-4 bg-slate-50">
                <div className="bg-white w-full max-w-md p-8 rounded-3xl shadow-xl border border-slate-200 animate-in slide-in-from-bottom-4">
                    <div className="text-center mb-8">
                        <div className="inline-block p-3 bg-brand-light rounded-2xl mb-4 text-brand-primary">
                            <Building2 size={32} />
                        </div>
                        <h2 className="text-2xl font-black text-slate-900">
                            {isResetMode ? "Reset Password" : `Join ${orgName}`}
                        </h2>
                        <p className="text-slate-500 text-sm mt-2">
                            {isResetMode 
                                ? "We'll send you a secure reset link." 
                                : staffMode
                                    ? `Team staff sign-up for ${team}`
                                    : isLoginMode
                                        ? "Log in to accept invitation"
                                        : orgIsCorporate ? "Join your team's hub" : "Secure your spot on the team"}
                        </p>
                    </div>

                    {errorMsg && !isResetMode && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-bold rounded-xl text-center">{errorMsg}</div>}
                    {resetMessage && isResetMode && (
                        <div className={`mb-4 p-3 border text-sm font-bold rounded-lg text-center ${resetMessageType === 'success' ? 'bg-green-50 border-green-100 text-green-600' : 'bg-red-50 border-red-100 text-red-600'}`}>
                            {resetMessage}
                        </div>
                    )}

                    {!isResetMode ? (
                        <>
                            <form onSubmit={handleAuthSubmit} className="space-y-4">
                                <div>
                                    <input 
                                        type="email" 
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary" 
                                        placeholder="Email Address" 
                                        value={email} 
                                        onChange={e => setEmail(e.target.value)} 
                                        required 
                                    />
                                    {!isLoginMode && (
                                        <p className="mt-1.5 px-1 text-[10px] text-slate-400 leading-snug">Setting up a player who doesn't have an email? Use yours with a plus tag, like <span className="font-mono">parent+jake@gmail.com</span>. It still arrives in your inbox, and it keeps each player's account separate.</p>
                                    )}
                                    {isRecognizedAdmin && (
                                        <div className="mt-2 flex items-center gap-2 text-xs text-purple-600 font-bold px-1">
                                            <ShieldCheck size={14} /> Recognized as Admin
                                        </div>
                                    )}
                                    {isRecognizedCoach && (
                                        <div className="mt-2 flex items-center gap-2 text-xs text-blue-600 font-bold px-1">
                                            <ShieldCheck size={14} /> Recognized as Coach: {matchedCoach?.groupId}
                                        </div>
                                    )}
                                </div>
                                
                                <div>
                                    <input 
                                        type="password" 
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary" 
                                        placeholder="Password" 
                                        value={password} 
                                        onChange={e => setPassword(e.target.value)} 
                                        required 
                                    />
                                    {isLoginMode && (
                                        <div className="flex justify-end mt-1">
                                            <button 
                                                type="button" 
                                                onClick={() => { setIsResetMode(true); setErrorMsg(''); setResetMessage(''); }}
                                                className="text-xs font-bold text-brand-primary hover:underline"
                                            >
                                                Forgot Password?
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                {/* 1. PRIMARY ACTION BUTTON */}
                                <button 
                                    type="submit" 
                                    disabled={isProcessing} 
                                    className="w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-lg hover:bg-brand-dark transition-all shadow-xl shadow-brand-primary/30 flex justify-center items-center gap-2 mt-6 disabled:opacity-50"
                                >
                                    {isLoginMode ? <LogIn size={24}/> : <UserPlus size={24}/>}
                                    {isLoginMode ? "Sign In & Join" : "Create Account"}
                                </button>
                                
                                {/* 2. MASSIVE SECONDARY TOGGLE BUTTON */}
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setIsLoginMode(!isLoginMode);
                                        setErrorMsg('');
                                    }}
                                    className="w-full py-4 mt-4 bg-slate-100 text-slate-800 rounded-2xl font-black text-lg border-4 border-slate-200 hover:bg-slate-200 hover:border-slate-300 transition-all flex justify-center items-center shadow-sm"
                                >
                                    {isLoginMode ? "Create a New Account" : "I Already Have an Account"}
                                </button>
                            </form>
                        </>
                    ) : (
                        <form onSubmit={handlePasswordReset} className="space-y-4">
                            <div>
                                <input 
                                    type="email" 
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary" 
                                    placeholder="Email Address" 
                                    value={email} 
                                    onChange={e => setEmail(e.target.value)} 
                                    required 
                                />
                            </div>
                            <button type="submit" disabled={isProcessing} className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold transition-all shadow-lg flex justify-center items-center gap-2 mt-4 hover:bg-black disabled:opacity-50">
                                {isProcessing ? 'Sending...' : 'Send Reset Link'}
                            </button>
                            <div className="mt-6 text-center">
                                <button type="button" onClick={() => setIsResetMode(false)} className="text-sm font-bold text-slate-400 hover:text-brand-primary transition-colors">
                                    &larr; Back to Login
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    if (step === 'persona') {
        return (
            <div className="flex flex-col items-center justify-center h-full p-4 bg-slate-50">
                <div className="bg-white w-full max-w-md p-8 rounded-3xl shadow-xl border border-slate-200 animate-in slide-in-from-bottom-4">
                    <div className="text-center mb-8">
                        <div className="inline-block p-3 bg-brand-light rounded-2xl mb-4 text-brand-primary">
                            <Users size={32} />
                        </div>
                        <h2 className="text-2xl font-black text-slate-900">Who are you joining as?</h2>
                        <p className="text-slate-500 text-sm mt-2">This tailors {orgName} to you — we'll confirm with your age on the next step.</p>
                    </div>

                    {errorMsg && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-bold rounded-xl text-center">{errorMsg}</div>}

                    <div className="space-y-3">
                        {(orgMemberTypes.length ? orgMemberTypes : resolveMemberTypes(null)).map(mt => (
                            <button
                                key={mt.id}
                                onClick={() => { setSelectedMemberType(mt.id); setErrorMsg(''); setStep('profile'); }}
                                className="w-full p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl text-left font-bold text-slate-800 hover:border-brand-primary hover:bg-brand-light/40 transition-all flex items-center justify-between group"
                            >
                                {mt.label}
                                <ArrowRight size={18} className="text-slate-300 group-hover:text-brand-primary transition-colors" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'gate_confirm') {
        return (
            <div className="flex flex-col items-center justify-center h-full p-4 bg-slate-50">
                <div className="bg-white w-full max-w-md p-8 rounded-3xl shadow-xl border border-slate-200 animate-in slide-in-from-bottom-4 text-center">
                    <div className="inline-block p-4 bg-amber-50 text-amber-600 rounded-full mb-4">
                        <Info size={32} />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 mb-2">Quick confirmation</h2>
                    <p className="text-slate-500 mb-6">
                        You're 18 or older, so you'll get the full adult experience. We'll still note you as family of a member. Sound right?
                    </p>

                    {errorMsg && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-bold rounded-xl text-center">{errorMsg}</div>}

                    <button
                        onClick={() => finalizeJoin(parseInt(age), 'adult')}
                        className="w-full py-4 bg-brand-primary text-white rounded-xl font-bold hover:bg-brand-dark transition-all shadow-lg shadow-brand-primary/30 flex justify-center items-center gap-2 mb-4"
                    >
                        Yes, continue <ArrowRight size={18} />
                    </button>
                    <button
                        onClick={() => { setErrorMsg(''); setStep('persona'); }}
                        className="w-full py-4 bg-white border-2 border-slate-200 text-slate-600 rounded-xl font-bold hover:border-slate-400 transition-all"
                    >
                        Go back &amp; change
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'profile') {
        return (
            <div className="flex flex-col items-center justify-center min-h-full p-4 py-8 bg-slate-50">
                {showTermsModal && <TermsModal type={showTermsModal} onClose={() => setShowTermsModal(null)} />}
                
                <div className="bg-white w-full max-w-md p-8 rounded-3xl shadow-xl border border-slate-200 animate-in slide-in-from-right-8">
                    <div className="text-center mb-6">
                        <h2 className="text-2xl font-black text-slate-900">User Profile</h2>
                        <p className="text-slate-500 text-sm mt-2">Help us personalize your experience.</p>
                    </div>

                    {errorMsg && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-bold rounded-xl text-center">{errorMsg}</div>}

                    {isRecognizedCoach && (
                        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl text-sm font-bold flex items-center justify-center gap-2 animate-in fade-in">
                            <ShieldCheck size={18} />
                            Team staff for: {listTeams(matchedCoach.teams)}
                        </div>
                    )}

                    <form onSubmit={handleProfileSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Full Name</label>
                            <div className="relative">
                                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input className="w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary" placeholder="e.g. John Doe" value={fullName} onChange={e => setFullName(e.target.value)} required />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1 flex items-center gap-1">
                                    Age <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input type="number" className="w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary" placeholder="18" value={age} onChange={e => setAge(e.target.value)} required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Gender</label>
                                <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary" value={gender} onChange={e => setGender(e.target.value)} required>
                                    <option value="" disabled>Select...</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Non-Binary">Non-Binary</option>
                                    <option value="Prefer not to say">Prefer not to say</option>
                                </select>
                            </div>
                        </div>

                        {/* Only show Squad selection to normal users if Squad Hub is enabled */}
                        {isSquadHubEnabled && orgGroups.length > 0 && !isRecognizedCoach && !isRecognizedAdmin && (
                            <div className="pt-2 border-t border-slate-100 animate-in fade-in slide-in-from-top-2">
                                <TeamPicker groups={orgGroups} value={selectedSquad} onChange={setSelectedSquad} age={noteAge} locked={!!(team && orgGroups.includes(team) && selectedSquad === team)} />
                            </div>
                        )}

                        <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3 flex gap-3 animate-in fade-in">
                            <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
                            <p className="text-[10px] text-blue-700 leading-tight">
                                <strong>Why we ask.</strong> Your age sets what you see here and keeps the content right for you. Where you live picks the support lines we show.
                            </p>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Country</label>
                            <div className="relative">
                                <Globe size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <select className="w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary" value={country} onChange={e => { setCountry(e.target.value); setRegion(''); }} required>
                                    <option value="" disabled>Select Country...</option>
                                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>

                        {(country === 'Canada' || country === 'United States') && (
                            <div className="animate-in fade-in slide-in-from-top-2">
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">{country === 'Canada' ? 'Province/Territory' : 'State'}</label>
                                <div className="relative">
                                    <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <select className="w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none focus:ring-2 focus:ring-brand-primary" value={region} onChange={e => setRegion(e.target.value)} required>
                                        <option value="" disabled>Select...</option>
                                        {country === 'Canada' ? CAN_PROVINCES.map(p => <option key={p} value={p}>{p}</option>) : US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                            </div>
                        )}

                        {['kid', 'teen'].includes(ageTier(age)) && !isRecognizedCoach && !isRecognizedAdmin && (
                            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 space-y-3 animate-in fade-in">
                                <div className="flex gap-3">
                                    <ShieldCheck size={18} className="text-amber-600 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-black text-slate-900">Parent or guardian</p>
                                        <p className="text-[11px] text-amber-800 leading-snug mt-0.5">
                                            {ageTier(age) === 'kid'
                                                ? `A parent or guardian sets this account up with the ${kid} and consents to it.`
                                                : 'A parent or guardian reviews and consents to this account.'}
                                            {' '}We keep this as the consent record. Coaches never see it.
                                        </p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <input className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-brand-primary" placeholder="Parent / guardian full name" value={guardian.name} onChange={e => setGuardian({ ...guardian, name: e.target.value })} />
                                    <select className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-brand-primary" value={guardian.relationship} onChange={e => setGuardian({ ...guardian, relationship: e.target.value })}>
                                        <option value="Parent">Parent</option>
                                        <option value="Legal guardian">Legal guardian</option>
                                        <option value="Other">{`Other adult responsible for the ${kid}`}</option>
                                    </select>
                                    <input type="email" className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-brand-primary" placeholder="Parent / guardian email" value={guardian.email} onChange={e => setGuardian({ ...guardian, email: e.target.value })} />
                                    <input type="tel" className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-brand-primary" placeholder="Phone (optional)" value={guardian.phone} onChange={e => setGuardian({ ...guardian, phone: e.target.value })} />
                                </div>
                                <label className="flex items-start gap-3 text-xs text-slate-700 leading-relaxed cursor-pointer">
                                    <input type="checkbox" className="mt-0.5 w-4 h-4 text-brand-primary border-slate-300 rounded focus:ring-brand-primary cursor-pointer shrink-0" checked={guardian.consent} onChange={e => setGuardian({ ...guardian, consent: e.target.checked })} />
                                    <span>I am this player's parent or legal guardian. I consent to their participation, including the check-ins and the content, as described in the <button type="button" onClick={() => setShowTermsModal('parental')} className="text-brand-primary font-bold hover:underline">Parental Consent</button>.</span>
                                </label>
                                <label className="flex items-start gap-3 text-xs text-slate-600 leading-relaxed cursor-pointer">
                                    <input type="checkbox" className="mt-0.5 w-4 h-4 text-brand-primary border-slate-300 rounded focus:ring-brand-primary cursor-pointer shrink-0" checked={guardian.emailOptIn} onChange={e => setGuardian({ ...guardian, emailOptIn: e.target.checked })} />
                                    <span>Email me the monthly team focus and the talk guides. Team-level only, never anything about my {kid}.</span>
                                </label>
                            </div>
                        )}

                        <div className="pt-2">
                            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <div className="relative flex items-center h-5">
                                    <input
                                        id="terms"
                                        type="checkbox" 
                                        className="w-4 h-4 text-brand-primary border-slate-300 rounded focus:ring-brand-primary cursor-pointer"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                    />
                                </div>
                                <div className="text-xs text-slate-600 leading-relaxed">
                                    <label htmlFor="terms" className="cursor-pointer">
                                        I agree to the {' '}
                                    </label>
                                    <button 
                                        type="button" 
                                        onClick={() => setShowTermsModal('anonymous')} 
                                        className="text-brand-primary font-bold hover:underline"
                                    >
                                        Data Policy
                                    </button>
                                    .
                                </div>
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            disabled={!termsAccepted}
                            className={`w-full py-4 bg-slate-900 text-white rounded-xl font-bold transition-all shadow-lg flex justify-center items-center gap-2 mt-2 ${!termsAccepted ? 'opacity-50 cursor-not-allowed' : 'hover:bg-black'}`}
                        >
                            Complete Profile <ArrowRight size={20}/>
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return <div className="text-center p-12 text-red-500 font-bold">{errorMsg || "Unknown Error"}</div>;
};

export default JoinOrgHandler;